import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, DatePicker, Space, Select, Spin, Tag, Empty } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { UserOutlined, FileTextOutlined, CalendarOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import * as actions from 'store/actions/AuthActions';
import useIsMobile from '../../hooks/useIsMobile';
import ReportFilterModal, { MobileFilterButton } from '../../components/Reports/ReportFilterModal';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import useUrlFilters from '../../hooks/useUrlFilters';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Filters live in the URL so a filtered report can be shared as a link. With no
// dates in the URL the range defaults to the selected employee's first update..today.
const URL_FILTERS = {
  employee: { type: 'string' },
  range: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 10 },
};

const EmployeePerformanceReport = () => {
  const isMobile = useIsMobile();
  const reportRef = useRef(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  // Default range computed for a given employee: { employee, range }.
  const [defaultRange, setDefaultRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const employeeFilter = filters.employee;
  // Changing employee drops the explicit range so it re-defaults to that employee's first update.
  const setEmployeeFilter = (value) => setFilters({ employee: value, range: null, page: 1 });
  const hasUrlRange = !!filters.range;
  const dateRange = useMemo(() => {
    if (filters.range) return filters.range;
    return defaultRange && defaultRange.employee === employeeFilter ? defaultRange.range : null;
  }, [filters.range, defaultRange, employeeFilter]);
  const [employees, setEmployees] = useState([]);

  // Fetch employees for filter
  const fetchEmployees = async () => {
    try {
      const response = await actions.loadAllEmployees();
      if (response && response.data) {
        setEmployees(response.data.results || response.data || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Fetch the first update date for the selected employee to set default start date
  const fetchFirstUpdateDate = useCallback(async (empId) => {
    try {
      const filters = {};
      if (empId) filters.employee = empId;
      // Fetch page 1 with size 1, ordering by oldest first isn't available via filter,
      // so we fetch all and rely on analytics or get the earliest from updates_per_day
      const analyticsRes = await actions.getDailyUpdatesAnalytics(filters);
      if (analyticsRes && analyticsRes.updates_per_day && analyticsRes.updates_per_day.length > 0) {
        const firstDate = dayjs(analyticsRes.updates_per_day[0].date);
        const today = dayjs();
        setDefaultRange({ employee: empId, range: [firstDate, today] });
        setAnalyticsData(analyticsRes);
      } else {
        // No updates, default to last 30 days
        setDefaultRange({ employee: empId, range: [dayjs().subtract(30, 'day'), dayjs()] });
        setAnalyticsData(analyticsRes);
      }
    } catch (error) {
      console.error('Error fetching first update date:', error);
      setDefaultRange({ employee: empId, range: [dayjs().subtract(30, 'day'), dayjs()] });
    }
  }, []);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    if (!dateRange) return;
    setLoading(true);
    try {
      const filters = {
        date_from: dateRange[0].format('YYYY-MM-DD'),
        date_to: dateRange[1].format('YYYY-MM-DD'),
      };
      if (employeeFilter) filters.employee = employeeFilter;
      const response = await actions.getDailyUpdatesAnalytics(filters);
      if (response) {
        setAnalyticsData(response);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, employeeFilter]);

  // Fetch daily updates list
  const fetchUpdates = useCallback(async (page = 1, pageSize = 10) => {
    if (!dateRange) return;
    setUpdatesLoading(true);
    try {
      const filters = {
        update_date_from: dateRange[0].format('YYYY-MM-DD'),
        update_date_to: dateRange[1].format('YYYY-MM-DD'),
      };
      if (employeeFilter) filters.employee = employeeFilter;
      const response = await actions.loadDailyUpdates(page, pageSize, filters);
      if (response) {
        setUpdates(response.results || []);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.count || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching updates:', error);
    } finally {
      setUpdatesLoading(false);
    }
  }, [dateRange, employeeFilter]);

  // Initial load
  useEffect(() => {
    fetchEmployees();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Without dates in the URL, default the range from the employee's first update
  useEffect(() => {
    if (!hasUrlRange) {
      fetchFirstUpdateDate(employeeFilter);
    }
  }, [employeeFilter, hasUrlRange, fetchFirstUpdateDate]);

  // Fetch analytics when date range or employee changes (no-op until a range is known)
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Fetch updates when filters or the page change
  useEffect(() => {
    fetchUpdates(filters.page, Math.min(filters.pageSize, MAX_PAGE_SIZE));
  }, [fetchUpdates, filters.page, filters.pageSize]);

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({ range: [dates[0], dates[1]], page: 1 });
    }
  };

  // Handle table change
  const handleTableChange = (newPagination) => {
    // A new page size starts again from page 1
    const sizeChanged = newPagination.pageSize !== filters.pageSize;
    setFilters({ page: sizeChanged ? 1 : newPagination.current, pageSize: newPagination.pageSize });
  };

  // Prepare chart data
  const getChartData = () => {
    if (!analyticsData?.updates_per_day) return [];
    return analyticsData.updates_per_day.map(item => ({
      date: dayjs(item.date).format('MMM DD'),
      updates: item.count,
    }));
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (employeeFilter) count++;
    if (dateRange) count++;
    return count;
  };

  // Table columns
  const columns = [
    {
      title: 'Date',
      dataIndex: 'update_date',
      key: 'update_date',
      width: 120,
      render: (date) => dayjs(date).format(isMobile ? 'MM/DD' : 'MMM DD, YYYY'),
      sorter: (a, b) => dayjs(a.update_date).unix() - dayjs(b.update_date).unix(),
    },
    {
      title: 'Employee',
      dataIndex: 'employee_name',
      key: 'employee_name',
      width: isMobile ? 120 : 180,
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: isMobile ? '12px' : '14px' }}>{name}</Text>
          {!isMobile && <Text type="secondary" style={{ fontSize: '12px' }}>{record.department}</Text>}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status) =>
        status === 'SUBMITTED'
          ? <Tag color="green">Submitted</Tag>
          : <Tag color="orange">Draft</Tag>,
    },
    {
      title: 'Tasks Completed',
      dataIndex: 'tasks_completed',
      key: 'tasks_completed',
      render: (text) => text ? (
        <div
          style={{ fontSize: isMobile ? '12px' : '13px' }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ) : <Text type="secondary">-</Text>,
    },
    {
      title: 'Challenges',
      dataIndex: 'challenges',
      key: 'challenges',
      responsive: ['md'],
      render: (text) => text ? (
        <div
          style={{ fontSize: isMobile ? '12px' : '13px' }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ) : <Text type="secondary">-</Text>,
    },
    {
      title: 'Plan for Tomorrow',
      dataIndex: 'plan_for_tomorrow',
      key: 'plan_for_tomorrow',
      responsive: ['lg'],
      render: (text) => text ? (
        <div
          style={{ fontSize: isMobile ? '12px' : '13px' }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ) : <Text type="secondary">-</Text>,
    },
  ];

  // Render additional filters for modal
  const renderAdditionalFilters = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Employee</Text>
        <Select
          value={employeeFilter}
          onChange={setEmployeeFilter}
          style={{ width: '100%' }}
          placeholder="All Employees"
          allowClear
          showSearch
          optionFilterProp="children"
        >
          {employees.map(emp => (
            <Option key={emp.id} value={emp.id}>
              {emp.user_name || emp.employee_id}
            </Option>
          ))}
        </Select>
      </div>
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Date Range</Text>
        <RangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
          format="YYYY-MM-DD"
          style={{ width: '100%' }}
          allowClear={false}
        />
      </div>
    </Space>
  );

  // Calculate days in range and submission rate
  const daysInRange = dateRange ? dateRange[1].diff(dateRange[0], 'day') + 1 : 0;
  const submissionRate = daysInRange > 0 && analyticsData
    ? Math.round((analyticsData.total_updates / daysInRange) * 100)
    : 0;

  // Tables for the PDF export. The updates table is paginated server-side, so
  // the export carries the page on screen and says so.
  const pdfTables = [
    {
      title: 'Updates Over Time',
      columns: [
        { header: 'Date', key: 'date' },
        { header: 'Updates', key: 'count', align: 'right' },
      ],
      rows: analyticsData?.updates_per_day || [],
    },
    {
      title: 'Daily Updates',
      note: 'Server-paginated: the rows currently loaded are included.',
      columns: [
        { header: 'Date', key: 'update_date' },
        { header: 'Employee', key: 'employee_name' },
        { header: 'Status', key: 'status' },
        { header: 'Tasks Completed', key: 'tasks_completed' },
        { header: 'Challenges', key: 'challenges' },
        { header: 'Plan for Tomorrow', key: 'plan_for_tomorrow' },
      ],
      rows: updates || [],
    },
  ];

  const pdfMeta = [
    { label: 'Total Updates', value: analyticsData?.total_updates || 0 },
    { label: 'Employees with Updates', value: `${analyticsData?.employees_with_updates || 0} / ${analyticsData?.total_employees || 0}` },
  ];

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5',
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>Employee Performance Report</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ExportPdfButton
            reportRef={reportRef}
            title="Employee Performance Report"
            subtitle={dateRange && dateRange[0] && dateRange[1]
              ? `${dateRange[0].format('YYYY-MM-DD')} to ${dateRange[1].format('YYYY-MM-DD')}`
              : 'All time'}
            meta={pdfMeta}
            tables={pdfTables}
            fileName="employee-performance-report"
            orientation="landscape"
            disabled={loading || !analyticsData}
          />
          {isMobile && (
            <MobileFilterButton
              onClick={() => setFilterModalVisible(true)}
              activeFiltersCount={getActiveFiltersCount()}
            />
          )}
        </div>
      </div>

      {/* Desktop Filters */}
      {!isMobile && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Space wrap size="middle" align="end">
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Employee:</Text>
              <Select
                value={employeeFilter}
                onChange={setEmployeeFilter}
                style={{ width: 250 }}
                size="small"
                placeholder="All Employees"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>
                    {emp.user_name || emp.employee_id}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Date Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                size="small"
                allowClear={false}
                placeholder={['Start Date', 'End Date']}
              />
            </div>
          </Space>
        </Card>
      )}

      {/* Mobile Filter Modal */}
      <ReportFilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        title="Performance Report Filters"
      >
        {renderAdditionalFilters()}
      </ReportFilterModal>

      {/* Statistics Cards */}
      <Spin spinning={loading}>
        <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
              <Statistic
                title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Updates</span>}
                value={analyticsData?.total_updates || 0}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#1890ff', fontSize: isMobile ? '16px' : '20px' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
              <Statistic
                title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Days in Range</span>}
                value={daysInRange}
                prefix={<CalendarOutlined />}
                valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
              <Statistic
                title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Submission Rate</span>}
                value={submissionRate}
                suffix="%"
                valueStyle={{
                  color: submissionRate >= 80 ? '#52c41a' : submissionRate >= 50 ? '#faad14' : '#ff4d4f',
                  fontSize: isMobile ? '16px' : '20px',
                }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
              <Statistic
                title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Employees</span>}
                value={analyticsData?.employees_with_updates || 0}
                suffix={`/ ${analyticsData?.total_employees || 0}`}
                prefix={<UserOutlined />}
                valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Chart */}
        <Card
          size="small"
          title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Updates Over Time</Text>}
          style={{ marginBottom: '16px' }}
        >
          {getChartData().length > 0 ? (
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
              <LineChart
                data={getChartData()}
                margin={{ top: 10, right: isMobile ? 10 : 30, left: isMobile ? 0 : 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  angle={isMobile ? -45 : 0}
                  textAnchor={isMobile ? 'end' : 'middle'}
                  height={isMobile ? 60 : 30}
                />
                <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} width={isMobile ? 30 : 40} />
                <RechartsTooltip />
                <Line
                  type="monotone"
                  dataKey="updates"
                  stroke="#1890ff"
                  strokeWidth={2}
                  dot={{ fill: '#1890ff', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                  activeDot={{ r: isMobile ? 5 : 6, fill: '#1890ff' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Empty description="No update data for the selected range" style={{ padding: '40px 0' }} />
          )}
        </Card>
      </Spin>

      {/* Updates Table */}
      <Card
        size="small"
        title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Daily Updates</Text>}
      >
        <Table
          columns={columns}
          dataSource={updates}
          rowKey="id"
          loading={updatesLoading}
          pagination={{
            ...pagination,
            defaultPageSize: isMobile ? 5 : 10,
            pageSizeOptions: ['5', '10', '20', '50'],
            showSizeChanger: !isMobile,
            showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total} updates`,
            position: ['bottomRight'],
            simple: isMobile,
          }}
          onChange={handleTableChange}
          size="small"
          scroll={{ x: isMobile ? 400 : 1000 }}
        />
      </Card>
    </div>
  );
};

export default EmployeePerformanceReport;
